import { HugeiconsIcon } from "@hugeicons/react";
import { ArrowDown01Icon, BrainIcon } from "@hugeicons/core-free-icons";
import type { ComponentProps } from 'react';
import { createContext, memo, useContext, useEffect, useState, useCallback } from 'react';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import { Streamdown } from 'streamdown';

type ReasoningContextValue = {
  isStreaming: boolean;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  duration: number;
};

const ReasoningContext = createContext<ReasoningContextValue | null>(null);

const useReasoning = () => {
  const context = useContext(ReasoningContext);
  if (!context) {
    throw new Error('Reasoning components must be used within Reasoning');
  }
  return context;
};

export type ReasoningProps = ComponentProps<typeof Collapsible> & {
  isStreaming?: boolean;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  duration?: number;
};

const AUTO_CLOSE_DELAY = 500;
const MS_IN_S = 1000;

export const Reasoning = memo(
  ({
    className,
    isStreaming = false,
    open: controlledOpen,
    defaultOpen = true,
    onOpenChange,
    duration: durationProp,
    children,
    ...props
  }: ReasoningProps) => {
    const [internalOpen, setInternalOpen] = useState(controlledOpen ?? defaultOpen);
    const [internalDuration, setInternalDuration] = useState(durationProp ?? 0);

    // Use controlled or internal state
    const isOpen = controlledOpen ?? internalOpen;
    const duration = durationProp ?? internalDuration;

    const setIsOpen = useCallback((value: boolean) => {
      setInternalOpen(value);
      onOpenChange?.(value);
    }, [onOpenChange]);

    const setDuration = useCallback((value: number) => {
      setInternalDuration(value);
    }, []);

    const [hasAutoClosedRef, setHasAutoClosedRef] = useState(false);
    const [startTime, setStartTime] = useState<number | null>(null);

    // Track duration when streaming starts and ends
    useEffect(() => {
      if (isStreaming) {
        if (startTime === null) {
          setStartTime(Date.now());
        }
      } else if (startTime !== null) {
        setDuration(Math.round((Date.now() - startTime) / MS_IN_S));
        setStartTime(null);
      }
    }, [isStreaming, startTime, setDuration]);

    // Auto-open when streaming starts, auto-close when streaming ends (once only)
    useEffect(() => {
      if (defaultOpen && !isStreaming && isOpen && !hasAutoClosedRef) {
        // Add a small delay before closing to allow user to see the content
        const timer = setTimeout(() => {
          setIsOpen(false);
          setHasAutoClosedRef(true);
        }, AUTO_CLOSE_DELAY);

        return () => clearTimeout(timer);
      }
    }, [isStreaming, isOpen, defaultOpen, setIsOpen, hasAutoClosedRef]);

    const handleOpenChange = (newOpen: boolean) => {
      setIsOpen(newOpen);
    };

    return (
      <ReasoningContext.Provider
        value={{ isStreaming, isOpen: isOpen ?? false, setIsOpen, duration: duration ?? 0 }}
      >
        <Collapsible
          className={cn('not-prose', className)}
          onOpenChange={handleOpenChange}
          open={isOpen}
          {...props}
        >
          {children}
        </Collapsible>
      </ReasoningContext.Provider>
    );
  }
);

export type ReasoningTriggerProps = ComponentProps<typeof CollapsibleTrigger>;

export const ReasoningTrigger = memo(
  ({ className, children, ...props }: ReasoningTriggerProps) => {
    const { isStreaming, isOpen, duration } = useReasoning();

    return (
      <CollapsibleTrigger
        className={cn(
          'flex items-center gap-1 rounded-lg px-2 py-1 text-caption text-gray-500 dark:text-gray-400 transition-colors hover:bg-gray-100 dark:hover:bg-gray-850 hover:text-gray-900 dark:hover:text-gray-100',
          className
        )}
        {...props}
      >
        {children ?? (
          <>
            <HugeiconsIcon icon={BrainIcon} className="size-3" />
            {isStreaming || duration === 0 ? (
              <span>Thinking</span>
            ) : (
              <span>{duration}s</span>
            )}
            <HugeiconsIcon icon={ArrowDown01Icon} className={cn(
              'size-2.5 transition-transform',
              isOpen ? 'rotate-180' : 'rotate-0'
            )} />
          </>
        )}
      </CollapsibleTrigger>
    );
  }
);

export type ReasoningContentProps = ComponentProps<typeof CollapsibleContent> & {
  children: string;
};

export const ReasoningContent = memo(
  ({ className, children, ...props }: ReasoningContentProps) => (
    <CollapsibleContent
      className={cn(
        'mt-1.5 text-caption text-gray-500 dark:text-gray-400 leading-relaxed',
        'data-[state=closed]:fade-out-0 data-[state=closed]:slide-out-to-top-2 data-[state=open]:slide-in-from-top-2 outline-hidden data-[state=closed]:animate-out data-[state=open]:animate-in',
        className
      )}
      {...props}
    >
      <div className="max-h-48 overflow-y-auto rounded-[20px] rounded-tl-[4px] bg-gray-50 dark:bg-gray-850 p-3 sm:p-2.5">
        <Streamdown className="grid gap-1 text-caption **:text-caption [&_li]:my-0 [&_ol]:my-1 [&_p]:my-0 [&_ul]:my-1">
          {children}
        </Streamdown>
      </div>
    </CollapsibleContent>
  )
);

Reasoning.displayName = 'Reasoning';
ReasoningTrigger.displayName = 'ReasoningTrigger';
ReasoningContent.displayName = 'ReasoningContent';
